/**
 * Layer Mapping API Service
 */

import apiClient from '../client';
import API_CONFIG from '../../config/api.config';
import type { APIResponse } from '../types';
import type {
  CreateObjectTypeRequest,
  UpdateObjectTypeRequest,
  ObjectTypeResponse,
  ListObjectTypesResponse,
  DeleteObjectTypeResponse,
  CreateLayerMappingsRequest,
  BatchCreateLayerMappingsResponse,
  ListLayerMappingsResponse,
  DeleteLayerMappingsResponse,
  LayerMappingStatsResponse,
} from '../types/layerMapping';

const { ENDPOINTS } = API_CONFIG;

/**
 * Object Type API
 */
export const objectTypeApi = {
  /**
   * 객체 타입 생성
   * @param projectId - 프로젝트 ID
   * @param data - 객체 타입 생성 요청 데이터
   * @returns 생성된 객체 타입 정보
   */
  async create(
    projectId: number,
    data: CreateObjectTypeRequest
  ): Promise<APIResponse<ObjectTypeResponse>> {
    return apiClient.post<ObjectTypeResponse>(
      ENDPOINTS.LAYER_MAPPING.OBJECT_TYPES(projectId),
      data
    );
  },

  /**
   * 객체 타입 목록 조회
   * @param projectId - 프로젝트 ID
   * @returns 객체 타입 목록
   */
  async getList(projectId: number): Promise<APIResponse<ListObjectTypesResponse>> {
    return apiClient.get<ListObjectTypesResponse>(
      ENDPOINTS.LAYER_MAPPING.OBJECT_TYPES(projectId)
    );
  },

  /**
   * 객체 타입 수정
   * @param projectId - 프로젝트 ID
   * @param id - 객체 타입 ID
   * @param data - 수정할 객체 타입 데이터
   * @returns 수정된 객체 타입 정보
   */
  async update(
    projectId: number,
    id: number,
    data: UpdateObjectTypeRequest
  ): Promise<APIResponse<ObjectTypeResponse>> {
    return apiClient.put<ObjectTypeResponse>(
      ENDPOINTS.LAYER_MAPPING.OBJECT_TYPE_BY_ID(projectId, id),
      data
    );
  },

  /**
   * 객체 타입 삭제 (소프트 삭제)
   * @param projectId - 프로젝트 ID
   * @param id - 객체 타입 ID
   * @returns 삭제 결과
   */
  async delete(
    projectId: number,
    id: number
  ): Promise<APIResponse<DeleteObjectTypeResponse>> {
    return apiClient.delete<DeleteObjectTypeResponse>(
      ENDPOINTS.LAYER_MAPPING.OBJECT_TYPE_BY_ID(projectId, id)
    );
  },
};

/**
 * Layer Type Mapping API (Optimized)
 * 레이어 단위 매핑으로 99.8% DB row 감소
 */
export const layerMappingApi = {
  /**
   * 레이어 타입 매핑 일괄 생성 (최적화)
   * @param projectId - 프로젝트 ID
   * @param floorId - 층 ID
   * @param data - 레이어 타입 매핑 생성 요청 데이터
   * @returns 생성된 레이어 타입 매핑 목록
   */
  async batchCreate(
    projectId: number,
    floorId: string,
    data: CreateLayerMappingsRequest
  ): Promise<APIResponse<BatchCreateLayerMappingsResponse>> {
    return apiClient.post<BatchCreateLayerMappingsResponse>(
      ENDPOINTS.LAYER_MAPPING.LAYER_TYPE_MAPPINGS(projectId, floorId),
      data
    );
  },

  /**
   * 레이어 타입 매핑 목록 조회
   * @param projectId - 프로젝트 ID
   * @param floorId - 층 ID
   * @returns 레이어 타입 매핑 목록
   */
  async getList(
    projectId: number,
    floorId: string
  ): Promise<APIResponse<ListLayerMappingsResponse>> {
    return apiClient.get<ListLayerMappingsResponse>(
      ENDPOINTS.LAYER_MAPPING.LAYER_TYPE_MAPPINGS(projectId, floorId)
    );
  },

  /**
   * 층별 레이어 타입 매핑 전체 삭제
   * @param projectId - 프로젝트 ID
   * @param floorId - 층 ID
   * @returns 삭제 결과
   */
  async deleteAll(
    projectId: number,
    floorId: string
  ): Promise<APIResponse<DeleteLayerMappingsResponse>> {
    return apiClient.delete<DeleteLayerMappingsResponse>(
      ENDPOINTS.LAYER_MAPPING.LAYER_TYPE_MAPPINGS(projectId, floorId)
    );
  },

  /**
   * 레이어 타입 매핑 통계 조회
   * @param projectId - 프로젝트 ID
   * @param floorId - 층 ID
   * @returns 레이어 타입 매핑 통계
   */
  async getStats(
    projectId: number,
    floorId: string
  ): Promise<APIResponse<LayerMappingStatsResponse>> {
    return apiClient.get<LayerMappingStatsResponse>(
      ENDPOINTS.LAYER_MAPPING.LAYER_TYPE_MAPPING_STATS(projectId, floorId)
    );
  },

  /**
   * 레이어의 엔티티 개수 업데이트
   * @param projectId - 프로젝트 ID
   * @param floorId - 층 ID
   * @param layerName - 레이어 이름
   * @param entityCount - 엔티티 개수
   * @returns 업데이트된 레이어 타입 매핑 정보
   */
  async updateEntityCount(
    projectId: number,
    floorId: string,
    layerName: string,
    entityCount: number
  ): Promise<APIResponse<any>> {
    return apiClient.put<any>(
      ENDPOINTS.LAYER_MAPPING.LAYER_TYPE_MAPPING_ENTITY_COUNT(projectId, floorId, layerName),
      { entity_count: entityCount }
    );
  },
};

export default {
  objectTypeApi,
  layerMappingApi,
};
